import React from 'react';
import { Icon } from './Icon.js';
import './primitives.css';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  readonly id?: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: React.ReactNode;
  readonly size?: ModalSize;
  readonly footer?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly closeLabel?: string;
  readonly 'data-testid'?: string;
}

/** Accessible SLDS-styled modal dialog. Closes on overlay click + Escape. */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  footer,
  children,
  closeLabel = 'Close',
  ...rest
}: ModalProps): React.ReactElement | null {
  const titleId = React.useId();
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="tl-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={['tl-modal', size !== 'md' ? `tl-modal--${size}` : ''].filter(Boolean).join(' ')}
        {...rest}
      >
        {title && (
          <header className="tl-modal__header">
            <h2 id={titleId} className="tl-modal__title">{title}</h2>
            <button type="button" className="tl-iconbutton" aria-label={closeLabel} onClick={onClose}>
              <Icon name="close" size={18} />
            </button>
          </header>
        )}
        <div className="tl-modal__body">{children}</div>
        {footer && <footer className="tl-modal__footer">{footer}</footer>}
      </div>
    </div>
  );
}
